import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import DepartmentAttentionDialog, { departmentAttention } from '../components/DepartmentAttentionDialog';
import { Link } from 'react-router-dom';
import { overlayStyle, modalStyle, inputStyle, btnPrimary, btnSecondary } from './AcademyShared';
import { getAuthHeaders } from '../utils/authHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const DepartmentList = () => {
    const companyId = localStorage.getItem('selectedCompanyId');
    const authConfig = () => ({ headers: getAuthHeaders() });
    const [departments, setDepartments] = useState([]);
    const [showAttention, setShowAttention] = useState(false);
    const [managerDept, setManagerDept] = useState(null);
    const [managerId, setManagerId] = useState('');
    const [managerError, setManagerError] = useState('');
    const [managerSaving, setManagerSaving] = useState(false);
    const assignManager = async () => {
        if (!managerId || managerSaving) return;
        setManagerSaving(true); setManagerError('');
        try {
            const [kind, id] = managerId.split(':');
            await axios.put(`${API_BASE}/companies/${companyId}/hr-departments/${managerDept.id}`, kind === 'company'
                ? { manager_user_id: Number(id) }
                : { manager_employee_id: Number(id) }, authConfig());
            setManagerDept(null); await fetchAll();
        } catch (err) { setManagerError(err.response?.data?.message || 'Unable to assign manager.'); }
        finally { setManagerSaving(false); }
    };
    const [unitTypes, setUnitTypes] = useState([]);
    const [hrProfiles, setHrProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('All Units');
    const [unitTypeFilter, setUnitTypeFilter] = useState('');

    const [showFormModal, setShowFormModal] = useState(false);
    const [editingDept, setEditingDept] = useState(null);
    const [formData, setFormData] = useState({
        name: '', code: '', description: '', unit_type_id: '',
        parent_id: '', hr_profile_id: '', approved_headcount: 1,
        status: 'Active', effective_date: ''
    });
    const [formError, setFormError] = useState(null);

    const [movingDept, setMovingDept] = useState(null);
    const [moveNewParentId, setMoveNewParentId] = useState('');
    const [moveProfileChoice, setMoveProfileChoice] = useState('inherit_new_parent');
    const [moveAssignProfileId, setMoveAssignProfileId] = useState('');
    const [movePreview, setMovePreview] = useState(null);
    const [movePreviewLoading, setMovePreviewLoading] = useState(false);

    const [deptToArchive, setDeptToArchive] = useState(null);
    const [archiveError, setArchiveError] = useState(null);
    const [deptToDelete, setDeptToDelete] = useState(null);
    const [deleteError, setDeleteError] = useState(null);

    const fetchAll = async () => {
        try {
            const [deptRes, unitTypeRes, profileRes] = await Promise.all([
                axios.get(`${API_BASE}/companies/${companyId}/hr-departments`, authConfig()),
                axios.get(`${API_BASE}/companies/${companyId}/unit-types`, authConfig()),
                axios.get(`${API_BASE}/companies/${companyId}/hr-profiles`, authConfig())
            ]);
            setDepartments(deptRes.data.data || []);
            setUnitTypes(unitTypeRes.data.data || []);
            setHrProfiles(profileRes.data.data || []);
        } catch (err) {
            setError('Failed to load departments.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, [companyId]);

    const updateForm = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    const openCreateModal = () => {
        setEditingDept(null);
        setFormData({
            name: '', code: '', description: '',
            unit_type_id: unitTypes.length > 0 ? String(unitTypes[0].id) : '',
            parent_id: '', hr_profile_id: '', approved_headcount: 1,
            status: 'Active', effective_date: ''
        });
        setFormError(null);
        setShowFormModal(true);
    };

    const openEditModal = (dept) => {
        setEditingDept(dept);
        setFormData({
            name: dept.name || '',
            code: dept.code || '',
            description: dept.description || '',
            unit_type_id: dept.unit_type ? String(dept.unit_type.id) : (dept.unit_type_id ? String(dept.unit_type_id) : ''),
            parent_id: dept.parent_id ? String(dept.parent_id) : '',
            hr_profile_id: dept.hr_profile_id ? String(dept.hr_profile_id) : '',
            approved_headcount: dept.approved_headcount || 1,
            status: dept.status || 'Active',
            effective_date: dept.effective_date || ''
        });
        setFormError(null);
        setShowFormModal(true);
    };

    const handleFormSubmit = async () => {
        if (!formData.name.trim()) { setFormError('Name is required.'); return; }
        if (editingDept && !formData.code.trim()) { setFormError('Code is required when editing a unit.'); return; }
        try {
            const payload = {
                name: formData.name,
                code: formData.code.trim() || undefined,
                description: formData.description || null,
                unit_type_id: formData.unit_type_id || null,
                parent_id: formData.parent_id || null,
                hr_profile_id: formData.hr_profile_id || null,
                approved_headcount: parseInt(formData.approved_headcount) || 1,
                status: formData.status,
                effective_date: formData.effective_date || null
            };
            if (editingDept) {
                await axios.put(`${API_BASE}/companies/${companyId}/hr-departments/${editingDept.id}`, payload, authConfig());
            } else {
                await axios.post(`${API_BASE}/companies/${companyId}/hr-departments`, payload, authConfig());
            }
            setShowFormModal(false);
            setFormError(null);
            fetchAll();
        } catch (err) {
            setFormError(err.response?.data?.message || 'Error saving department.');
        }
    };

    const handleConfirmArchive = async () => {
        if (!deptToArchive) return;
        try {
            await axios.post(`${API_BASE}/companies/${companyId}/hr-departments/${deptToArchive.id}/archive`, {}, authConfig());
            setDeptToArchive(null);
            setArchiveError(null);
            fetchAll();
        } catch (err) {
            setArchiveError(err.response?.data?.message || 'Error archiving department.');
        }
    };

    const handleConfirmDelete = async () => {
        if (!deptToDelete) return;
        try {
            await axios.delete(`${API_BASE}/companies/${companyId}/hr-departments/${deptToDelete.id}`, authConfig());
            setDeptToDelete(null);
            setDeleteError(null);
            fetchAll();
        } catch (err) {
            setDeleteError(err.response?.data?.message || 'Error deleting department.');
        }
    };

    const openMoveModal = (dept) => {
        setMovingDept(dept);
        setMoveNewParentId(dept.parent_id ? String(dept.parent_id) : '');
        setMoveProfileChoice('inherit_new_parent');
        setMoveAssignProfileId('');
        setMovePreview(null);
    };

    const handlePreviewMove = async () => {
        setMovePreviewLoading(true);
        try {
            const params = moveNewParentId ? { new_parent_id: moveNewParentId } : {};
            const res = await axios.get(`${API_BASE}/companies/${companyId}/hr-departments/${movingDept.id}/move-preview`, { params, headers: getAuthHeaders() });
            setMovePreview(res.data.data);
        } catch (err) { alert('Error generating preview.'); }
        finally { setMovePreviewLoading(false); }
    };

    const handleConfirmMove = async () => {
        try {
            await axios.post(`${API_BASE}/companies/${companyId}/hr-departments/${movingDept.id}/move-confirm`, {
                new_parent_id: moveNewParentId || null,
                profile_choice: moveProfileChoice,
                hr_profile_id: moveProfileChoice === 'assign' ? moveAssignProfileId : null
            }, authConfig());
            setMovingDept(null);
            setMovePreview(null);
            fetchAll();
        } catch (err) { alert('Error moving department.'); }
    };

    // Filtering
    let filteredDepts = [...departments];
    if (activeTab === 'Active') filteredDepts = filteredDepts.filter(d => d.status === 'Active');
    else if (activeTab === 'Draft') filteredDepts = filteredDepts.filter(d => d.status === 'Draft');
    else if (activeTab === 'Archived') filteredDepts = filteredDepts.filter(d => d.status === 'Archived');
    else filteredDepts = filteredDepts.filter(d => d.status !== 'Archived');

    if (unitTypeFilter) filteredDepts = filteredDepts.filter(d => d.unit_type && String(d.unit_type.id) === unitTypeFilter);

    if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredDepts = filteredDepts.filter(d =>
            d.name.toLowerCase().includes(term) ||
            d.code.toLowerCase().includes(term) ||
            (d.description && d.description.toLowerCase().includes(term)) ||
            (d.unit_type && d.unit_type.name.toLowerCase().includes(term))
        );
    }

    // Counts
    const totalCount = departments.length;
    const activeCount = departments.filter(d => d.status === 'Active').length;
    const draftCount = departments.filter(d => d.status === 'Draft').length;
    const archivedCount = departments.filter(d => d.status === 'Archived').length;
    const attentionItems = departmentAttention(departments);
    const attentionCount = attentionItems.length;

    // Styles
    const tabBtnStyle = (isActive) => ({
        padding: '8px 16px', borderRadius: '999px',
        border: isActive ? 'none' : '1px solid #cbd5e1',
        backgroundColor: isActive ? '#2563eb' : '#fff',
        color: isActive ? '#fff' : '#64748b',
        fontSize: '0.85rem', fontWeight: isActive ? '600' : '500',
        cursor: 'pointer', transition: 'all 0.2s'
    });

    const cardStyle = {
        backgroundColor: '#fff', border: '1px solid #e2e8f0',
        borderRadius: '8px', padding: '16px 20px'
    };

    const badgeStyle = (bg, color) => ({
        padding: '3px 10px', borderRadius: '999px', fontSize: '0.75rem',
        fontWeight: '600', display: 'inline-block', backgroundColor: bg, color: color
    });

    const inputStyle = {
        width: '100%', padding: '8px 12px', borderRadius: '6px',
        border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none',
        backgroundColor: '#fff', color: '#0f172a', boxSizing: 'border-box'
    };

    const labelStyle = {
        display: 'block', fontSize: '0.8rem', fontWeight: '600',
        color: '#64748b', marginBottom: '4px'
    };

    const modalOverlay = {
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000
    };

    const actionBtnStyle = (color) => ({
        background: 'none', border: 'none', color: color,
        fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer',
        padding: '4px 0', textDecoration: 'none'
    });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Active': return badgeStyle('#d1fae5', '#065f46');
            case 'Draft': return badgeStyle('#fef3c7', '#92400e');
            case 'Inactive': return badgeStyle('#fee2e2', '#991b1b');
            case 'Archived': return badgeStyle('#f1f5f9', '#475569');
            default: return badgeStyle('#f1f5f9', '#475569');
        }
    };

    const getManagerBadge = (status) => {
        if (status === 'OK') return badgeStyle('#d1fae5', '#065f46');
        return badgeStyle('#fee2e2', '#991b1b');
    };

    if (initialLoading) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading departments...</div>;
    if (error) return <div style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>{error}</div>;

    return (
        <div style={{ padding: '40px' }}>
            {showAttention && <DepartmentAttentionDialog items={attentionItems} onClose={() => setShowAttention(false)} />}
            {managerDept && <div style={overlayStyle}>
                <div role="dialog" aria-label="Assign Manager" style={modalStyle}>
                    <h3>Assign Manager — {managerDept.name}</h3>
                    <p>Choose the company manager directly, or an active employee in this department. An employee manager's position becomes the department's manager position.</p>
                    <select aria-label="Manager" style={inputStyle} value={managerId} onChange={e => setManagerId(e.target.value)} disabled={managerSaving}>
                        <option value="">Select manager</option>
                        {managerDept.company_manager && <option value={`company:${managerDept.company_manager.id}`}>Company manager — {managerDept.company_manager.name}</option>}
                        {(managerDept.manager_candidates || []).map(employee => <option key={employee.id} value={`employee:${employee.id}`}>{employee.name} {employee.code ? `(${employee.code})` : ''}</option>)}
                    </select>
                    {!managerDept.manager_candidates?.length && !managerDept.company_manager && <p>No eligible managers yet. Set the company manager, or create a position under <Link to="/company/positions">Positions</Link> and assign an active employee to it under <Link to="/company/employees">Employees</Link>.</p>}
                    {managerError && <p role="alert" style={{ color: '#dc2626' }}>{managerError}</p>}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                        <button style={btnSecondary} disabled={managerSaving} onClick={() => setManagerDept(null)}>Cancel</button>
                        <button style={btnPrimary} disabled={!managerId || managerSaving} onClick={assignManager}>{managerSaving ? 'Saving…' : 'Save Manager'}</button>
                    </div>
                </div>
            </div>}
            {/* Page Title */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' }}>Departments & Organizational Units</h1>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>{totalCount} total unit{totalCount !== 1 ? 's' : ''}</p>
                </div>
                <button onClick={openCreateModal} style={{
                    padding: '10px 20px', backgroundColor: '#2563eb', color: '#fff', border: 'none',
                    borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer'
                }}>
                    + Create New Unit
                </button>
            </div>

            {/* Warning */}
            {unitTypes.length === 0 && (
                <div style={{
                    backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px',
                    padding: '12px 16px', marginBottom: '20px', fontSize: '0.85rem', color: '#854d0e'
                }}>
                    No unit types exist yet. Visit the Unit Types page to create some (e.g. Division, Department, Team) before creating departments.
                </div>
            )}

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={cardStyle}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Total Units</h3>
                    <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#0f172a' }}>{totalCount}</p>
                </div>
                <div style={cardStyle}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Active</h3>
                    <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#10b981' }}>{activeCount}</p>
                </div>
                <div style={cardStyle}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Draft</h3>
                    <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#eab308' }}>{draftCount}</p>
                </div>
                <div style={cardStyle}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Archived</h3>
                    <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#64748b' }}>{archivedCount}</p>
                </div>
                <button type="button" aria-haspopup="dialog" onClick={() => setShowAttention(true)} style={{ ...cardStyle, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Needs Attention</h3>
                    <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: attentionCount > 0 ? '#dc2626' : '#10b981' }}>{attentionCount}</p>
                </button>
            </div>

            {/* Filter Bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {['All Units', 'Active', 'Draft', 'Archived'].map(tab => (
                        <button key={tab} style={tabBtnStyle(activeTab === tab)} onClick={() => setActiveTab(tab)}>{tab}</button>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select
                        value={unitTypeFilter}
                        onChange={(e) => setUnitTypeFilter(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', outline: 'none', cursor: 'pointer', backgroundColor: '#fff', color: '#0f172a' }}
                    >
                        <option value="">All Unit Types</option>
                        {unitTypes.map(ut => <option key={ut.id} value={String(ut.id)}>{ut.name}</option>)}
                    </select>
                    <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>🔍</span>
                        <input
                            type="text" placeholder="Search units..."
                            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            style={{ ...inputStyle, width: '220px', paddingLeft: '34px' }}
                            onFocus={e => e.target.style.borderColor = '#2563eb'}
                            onBlur={e => e.target.style.borderColor = '#cbd5e1'}
                        />
                    </div>
                </div>
            </div>

            {/* Result Count */}
            {(searchTerm || activeTab !== 'All Units' || unitTypeFilter) && (
                <p style={{ margin: '0 0 12px 0', fontSize: '0.82rem', color: '#64748b' }}>
                    Showing {filteredDepts.length} of {totalCount} units
                </p>
            )}

            {/* Departments Table */}
            <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Name / Code', 'Unit Type', 'Parent', 'HR Profile', 'Status', 'Manager', 'Headcount', 'Actions'].map(h => (
                                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.78rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDepts.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontStyle: 'italic' }}>
                                        {searchTerm || unitTypeFilter ? 'No units match your filters.' : 'No departments found. Create your first one!'}
                                    </td>
                                </tr>
                            ) : (
                                filteredDepts.map(dept => {
                                    const isArchived = dept.status === 'Archived';
                                    const isDraft = dept.status === 'Draft';
                                    return (
                                        <tr key={dept.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s', cursor: 'default' }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>

                                            {/* Name / Code */}
                                            <td style={{ padding: '14px 16px' }}>
                                                <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '0.92rem', marginBottom: '2px' }}>{dept.name}</div>
                                                <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontFamily: 'monospace' }}>{dept.code}</div>
                                                {dept.description && (
                                                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {dept.description}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Unit Type */}
                                            <td style={{ padding: '14px 16px', fontSize: '0.88rem', color: '#334155' }}>
                                                {dept.unit_type ? dept.unit_type.name : '—'}
                                            </td>

                                            {/* Parent */}
                                            <td style={{ padding: '14px 16px', fontSize: '0.88rem', color: '#334155' }}>
                                                {dept.parent ? dept.parent.name : <span style={{ color: '#94a3b8' }}>Top Level</span>}
                                            </td>

                                            {/* HR Profile */}
                                            <td style={{ padding: '14px 16px' }}>
                                                {dept.effective_hr_profile ? (
                                                    <div>
                                                        <span style={{ fontSize: '0.88rem', color: '#334155' }}>{dept.effective_hr_profile.name}</span>
                                                        {dept.profile_is_inherited && (
                                                            <span style={{ ...badgeStyle('#e0e7ff', '#3730a3'), marginLeft: '6px', fontSize: '0.68rem' }}>Inherited</span>
                                                        )}
                                                        {!dept.profile_is_inherited && dept.hr_profile_id && (
                                                            <span style={{ ...badgeStyle('#dbeafe', '#1e40af'), marginLeft: '6px', fontSize: '0.68rem' }}>Own</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span style={{ color: '#94a3b8', fontSize: '0.88rem' }}>None</span>
                                                )}
                                            </td>

                                            {/* Status */}
                                            <td style={{ padding: '14px 16px' }}>
                                                <span style={getStatusBadge(dept.status)}>{dept.status}</span>
                                            </td>

                                            {/* Manager */}
                                            <td style={{ padding: '14px 16px' }}>
                                                <span style={getManagerBadge(dept.manager_status)}>
                                                    {dept.manager_status === 'OK' ? (dept.manager_name || 'OK') : dept.manager_status === 'Primary Manager Not Set' ? 'Primary N/S' : 'Required'}
                                                </span>
                                                {!isArchived && <button style={{ display: 'block', marginTop: 6, border: 0, background: 'none', color: '#2563eb', cursor: 'pointer', padding: 0 }} onClick={() => { setManagerDept(dept); setManagerId(dept.manager_user_id ? `company:${dept.manager_user_id}` : (dept.manager_employee_id ? `employee:${dept.manager_employee_id}` : '')); setManagerError(''); }}>{dept.manager_employee_id || dept.manager_user_id ? 'Change Manager' : 'Assign Manager'}</button>}
                                            </td>

                                            {/* Headcount */}
                                            <td style={{ padding: '14px 16px' }}>
                                                {dept.headcount_warning ? (
                                                    <span style={badgeStyle('#fee2e2', '#991b1b')} title={dept.headcount_warning}>Over</span>
                                                ) : (
                                                    <span style={badgeStyle('#d1fae5', '#065f46')}>OK</span>
                                                )}
                                            </td>

                                            {/* Actions */}
                                            <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                    {!isArchived && (
                                                        <button onClick={() => openEditModal(dept)} style={actionBtnStyle('#2563eb')}>Edit</button>
                                                    )}
                                                    {!isArchived && (
                                                        <button onClick={() => openMoveModal(dept)} style={actionBtnStyle('#0f172a')}>Move</button>
                                                    )}
                                                    {!isArchived && !isDraft && (
                                                        <button onClick={() => { setDeptToArchive(dept); setArchiveError(null); }} style={actionBtnStyle('#f59e0b')}>Archive</button>
                                                    )}
                                                    {isDraft && (
                                                        <button onClick={() => { setDeptToDelete(dept); setDeleteError(null); }} style={actionBtnStyle('#dc2626')}>Delete</button>
                                                    )}
                                                    {!isDraft && !isArchived && (
                                                        <button onClick={() => { setDeptToDelete(dept); setDeleteError(null); }} style={actionBtnStyle('#dc2626')}>Delete</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ============ CREATE / EDIT MODAL ============ */}
            {showFormModal && (
                <div style={modalOverlay} onClick={() => setShowFormModal(false)}>
                    <div onClick={e => e.stopPropagation()} style={{
                        backgroundColor: '#fff', borderRadius: '8px', padding: '28px',
                        width: '960px', maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box',
                        maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>
                                {editingDept ? `Edit "${editingDept.name}"` : 'Create New Unit'}
                            </h2>
                            <button onClick={() => setShowFormModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#64748b' }}>×</button>
                        </div>

                        {formError && (
                            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.85rem', color: '#dc2626' }}>
                                {formError}
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '16px' }}>
                            {/* Fields reflow to the available dialog width. */}
                            <div>
                                <label style={labelStyle}>Name *</label>
                                <input type="text" value={formData.name} onChange={e => updateForm('name', e.target.value)}
                                    placeholder="e.g. Engineering" style={inputStyle} />
                            </div>

                            {/* Code */}
                            <div>
                                <label style={labelStyle}>Code <span style={{ fontWeight: '400', color: '#94a3b8' }}>{editingDept ? '(required)' : '(auto if blank)'}</span></label>
                                <input type="text" value={formData.code} onChange={e => updateForm('code', e.target.value)}
                                    placeholder="Auto-generated" style={inputStyle} />
                            </div>

                            {/* Unit Type */}
                            <div>
                                <label style={labelStyle}>Unit Type</label>
                                <select value={formData.unit_type_id} onChange={e => updateForm('unit_type_id', e.target.value)} style={inputStyle}>
                                    <option value="">None</option>
                                    {unitTypes.map(ut => <option key={ut.id} value={String(ut.id)}>{ut.name}</option>)}
                                </select>
                            </div>

                            {/* Parent Unit */}
                            <div>
                                <label style={labelStyle}>Parent Unit</label>
                                <select value={formData.parent_id} onChange={e => updateForm('parent_id', e.target.value)} style={inputStyle}>
                                    <option value="">None (Top Level)</option>
                                    {departments
                                        .filter(d => !editingDept || d.id !== editingDept.id)
                                        .map(d => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
                                </select>
                            </div>

                            {/* HR Profile */}
                            <div>
                                <label style={labelStyle}>HR Settings Profile</label>
                                <select value={formData.hr_profile_id} onChange={e => updateForm('hr_profile_id', e.target.value)} style={inputStyle}>
                                    <option value="">Inherit (parent / workspace default)</option>
                                    {hrProfiles.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                                </select>
                            </div>

                            {/* Status */}
                            <div>
                                <label style={labelStyle}>Status</label>
                                <select value={formData.status} onChange={e => updateForm('status', e.target.value)} style={inputStyle}>
                                    <option value="Active">Active</option>
                                    <option value="Draft">Draft</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            </div>

                            {/* Effective Date */}
                            <div>
                                <label style={labelStyle}>Effective Date</label>
                                <input type="date" value={formData.effective_date} onChange={e => updateForm('effective_date', e.target.value)} style={inputStyle} />
                            </div>

                            {/* Approved Headcount */}
                            <div>
                                <label style={labelStyle}>Approved Headcount</label>
                                <input type="number" min="0" value={formData.approved_headcount}
                                    onChange={e => updateForm('approved_headcount', e.target.value)} style={inputStyle} />
                            </div>

                            {/* Description - full width */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={labelStyle}>Description</label>
                                <textarea value={formData.description} onChange={e => updateForm('description', e.target.value)}
                                    placeholder="Optional description..." rows={3}
                                    style={{ ...inputStyle, resize: 'vertical' }} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
                            <button onClick={() => setShowFormModal(false)} style={{
                                padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1',
                                backgroundColor: '#fff', color: '#334155', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}>Cancel</button>
                            <button onClick={handleFormSubmit} style={{
                                padding: '8px 20px', borderRadius: '6px', border: 'none',
                                backgroundColor: '#2563eb', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}>{editingDept ? 'Save Changes' : 'Create Unit'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============ MOVE MODAL ============ */}
            {movingDept && (
                <div style={modalOverlay} onClick={() => { setMovingDept(null); setMovePreview(null); }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        backgroundColor: '#fff', borderRadius: '8px', padding: '24px',
                        width: '500px', maxHeight: '85vh', overflowY: 'auto'
                    }}>
                        <h3 style={{ margin: '0 0 20px 0', fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>
                            Move "{movingDept.name}"
                        </h3>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>New Parent Unit</label>
                            <select value={moveNewParentId}
                                onChange={e => { setMoveNewParentId(e.target.value); setMovePreview(null); }}
                                style={inputStyle}>
                                <option value="">None (Top Level)</option>
                                {departments.filter(d => d.id !== movingDept.id).map(d =>
                                    <option key={d.id} value={String(d.id)}>{d.name}</option>
                                )}
                            </select>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>HR Settings Profile</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: '#334155', cursor: 'pointer' }}>
                                    <input type="radio" checked={moveProfileChoice === 'keep'} onChange={() => setMoveProfileChoice('keep')} />
                                    Keep current ({movingDept.effective_hr_profile ? movingDept.effective_hr_profile.name : 'None'})
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: '#334155', cursor: 'pointer' }}>
                                    <input type="radio" checked={moveProfileChoice === 'inherit_new_parent'} onChange={() => setMoveProfileChoice('inherit_new_parent')} />
                                    Inherit from new parent
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: '#334155', cursor: 'pointer' }}>
                                    <input type="radio" checked={moveProfileChoice === 'assign'} onChange={() => setMoveProfileChoice('assign')} />
                                    Assign a different profile
                                </label>
                                {moveProfileChoice === 'assign' && (
                                    <select value={moveAssignProfileId} onChange={e => setMoveAssignProfileId(e.target.value)}
                                        style={{ ...inputStyle, marginLeft: '24px', width: 'calc(100% - 24px)' }}>
                                        <option value="">Select a profile...</option>
                                        {hrProfiles.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                                    </select>
                                )}
                            </div>
                        </div>

                        <button onClick={handlePreviewMove} disabled={movePreviewLoading} style={{
                            width: '100%', padding: '10px', backgroundColor: '#0f172a', color: '#fff',
                            border: 'none', borderRadius: '6px', fontSize: '0.88rem', fontWeight: '600',
                            cursor: 'pointer', marginBottom: '16px', opacity: movePreviewLoading ? 0.6 : 1
                        }}>
                            {movePreviewLoading ? 'Loading preview...' : 'Preview Code Changes'}
                        </button>

                        {movePreview && (
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '8px' }}>
                                    These codes will change. Nothing is saved yet.
                                </p>
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f8fafc' }}>
                                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#64748b' }}>Unit</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#64748b' }}>Current</th>
                                                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600', color: '#64748b' }}>Proposed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {movePreview.map(row => (
                                                <tr key={row.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '8px 12px', color: '#334155' }}>{row.name}</td>
                                                    <td style={{ padding: '8px 12px', color: '#94a3b8', fontFamily: 'monospace' }}>{row.current_code}</td>
                                                    <td style={{ padding: '8px 12px', color: '#2563eb', fontWeight: '600', fontFamily: 'monospace' }}>{row.proposed_code}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button onClick={() => { setMovingDept(null); setMovePreview(null); }} style={{
                                padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1',
                                backgroundColor: '#fff', color: '#334155', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}>Cancel</button>
                            <button onClick={handleConfirmMove} disabled={!movePreview} style={{
                                padding: '8px 20px', borderRadius: '6px', border: 'none',
                                backgroundColor: '#2563eb', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem',
                                opacity: movePreview ? 1 : 0.4, cursor: movePreview ? 'pointer' : 'not-allowed'
                            }}>Confirm Move</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============ ARCHIVE MODAL ============ */}
            {deptToArchive && (
                <div style={modalOverlay} onClick={() => { setDeptToArchive(null); setArchiveError(null); }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        backgroundColor: '#fff', borderRadius: '8px', padding: '24px',
                        width: '420px', textAlign: 'center'
                    }}>
                        <h2 style={{ fontSize: '1.15rem', fontWeight: '700', color: '#0f172a', margin: '0 0 12px 0' }}>Archive Unit?</h2>
                        <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 8px 0' }}>
                            Are you sure you want to archive <strong>{deptToArchive.name}</strong>?
                        </p>
                        <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: '0 0 20px 0' }}>
                            New employee assignments will be blocked. History and records are preserved.
                        </p>
                        {archiveError && (
                            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.85rem', color: '#dc2626', textAlign: 'left' }}>
                                {archiveError}
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                            <button onClick={() => { setDeptToArchive(null); setArchiveError(null); }} style={{
                                padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1',
                                backgroundColor: '#fff', color: '#334155', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}>Cancel</button>
                            <button onClick={handleConfirmArchive} style={{
                                padding: '8px 20px', borderRadius: '6px', border: 'none',
                                backgroundColor: '#f59e0b', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}>Archive</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============ DELETE MODAL ============ */}
            {deptToDelete && (
                <div style={modalOverlay} onClick={() => { setDeptToDelete(null); setDeleteError(null); }}>
                    <div onClick={e => e.stopPropagation()} style={{
                        backgroundColor: '#fff', borderRadius: '8px', padding: '24px',
                        width: '420px', textAlign: 'center'
                    }}>
                        <h2 style={{ fontSize: '1.15rem', fontWeight: '700', color: '#dc2626', margin: '0 0 12px 0' }}>Delete Unit?</h2>
                        <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 8px 0' }}>
                            This will permanently delete <strong>{deptToDelete.name}</strong> and all its data.
                        </p>
                        <p style={{ color: '#dc2626', fontSize: '0.82rem', fontWeight: '600', margin: '0 0 20px 0' }}>
                            This cannot be undone. Only draft units with no employees or positions can be deleted.
                        </p>
                        {deleteError && (
                            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.85rem', color: '#dc2626', textAlign: 'left' }}>
                                {deleteError}
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                            <button onClick={() => { setDeptToDelete(null); setDeleteError(null); }} style={{
                                padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1',
                                backgroundColor: '#fff', color: '#334155', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}>Cancel</button>
                            <button onClick={handleConfirmDelete} style={{
                                padding: '8px 20px', borderRadius: '6px', border: 'none',
                                backgroundColor: '#dc2626', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DepartmentList;
